<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'
import AboutContactForm from '@/components/marketing/about/AboutContactForm.vue'
import { usePlatformContact } from '@/composables/usePlatformContact'
import { useAuth } from '@/composables/useAuth'
import { PlatformContactTopic } from '@/types/contact'

type CardKey = 'organizer' | 'participant' | 'other'

const { t } = useI18n()
const { infoEmail, supportEmail } = usePlatformContact()
const { isAuthenticated, isOrganizer, user } = useAuth()

// Most visitors are (prospective) organizers, so their card is open by default.
const selectedCard = ref<CardKey>('organizer')
// Participant card: the form only appears after the visitor confirms a platform issue.
const participantFormVisible = ref(false)

const cards = [
  { key: 'organizer', ring: 'ring-emerald-500', icon: 'M8 7V3m8 4V3M5 11h14M5 5h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z' },
  { key: 'participant', ring: 'ring-sky-500', icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
  { key: 'other', ring: 'ring-slate-400', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' },
] as const


// A logged-in participant (no organisation) gets their details prefilled.
const prefillName = computed(() =>
  isAuthenticated.value && !isOrganizer.value && user.value
    ? `${user.value.first_name} ${user.value.last_name}`.trim()
    : undefined,
)
const prefillEmail = computed(() =>
  isAuthenticated.value && !isOrganizer.value ? user.value?.email : undefined,
)

const formTopic = computed<PlatformContactTopic | null>(() => {
  if (selectedCard.value === 'organizer' && !isOrganizer.value) return PlatformContactTopic.EventPlanning
  if (selectedCard.value === 'participant' && participantFormVisible.value) return PlatformContactTopic.ParticipantAccountOrPayment
  if (selectedCard.value === 'other') return PlatformContactTopic.Other
  return null
})

const myRacesLink = computed(() => (isAuthenticated.value ? '/my/races' : '/my/login'))

// Identifies which panel is shown. Cards that render the same form share one key, so the
// form (and any typed content) is preserved when the visitor switches between them.
const panelKey = computed<string>(() => {
  if (selectedCard.value === 'organizer' && isOrganizer.value) return 'organizer-ticket'
  if (selectedCard.value === 'participant' && !participantFormVisible.value) return 'participant-intro'
  return 'form'
})

// Short status message for screen readers; the live region never wraps the form itself.
const liveMessage = computed<string>(() => t(`contact.router.${selectedCard.value}.title`))

function selectCard(key: CardKey): void {
  if (selectedCard.value === key) return
  selectedCard.value = key
  participantFormVisible.value = false
}
</script>

<template>
  <section id="contact" class="bg-slate-50 py-24" aria-labelledby="contact-title">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid grid-cols-1 lg:grid-cols-5 gap-12">
        <!-- Left: heading, router cards, direct details -->
        <div class="lg:col-span-2">
          <h2 id="contact-title" tabindex="-1" class="text-3xl md:text-4xl font-bold text-slate-900 focus:outline-none">
            {{ t('contact.title') }}
          </h2>
          <p class="mt-3 text-slate-600 leading-relaxed">{{ t('contact.subtitle') }}</p>

          <div class="mt-8 space-y-3" role="group" :aria-label="t('contact.router.group_label')">
            <button
              v-for="card in cards"
              :key="card.key"
              type="button"
              class="w-full min-h-11 text-left flex items-start gap-4 bg-white rounded-xl border border-slate-200 p-4 transition-all motion-safe:hover:-translate-y-0.5 hover:shadow-lg"
              :class="selectedCard === card.key ? `ring-2 ${card.ring}` : ''"
              :aria-pressed="selectedCard === card.key"
              @click="selectCard(card.key)"
            >
              <svg class="w-6 h-6 shrink-0 text-emerald-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="card.icon" />
              </svg>
              <span>
                <span class="block font-semibold text-slate-900">{{ t(`contact.router.${card.key}.title`) }}</span>
                <span class="block mt-0.5 text-sm text-slate-500">{{ t(`contact.router.${card.key}.body`) }}</span>
              </span>
            </button>
          </div>

          <div class="mt-8 border-t border-slate-200 pt-6 space-y-2 text-sm">
            <p>
              <span class="text-slate-500">{{ t('contact.details.info_label') }}</span>
              <a :href="`mailto:${infoEmail}`" class="ml-2 font-semibold text-emerald-600 hover:text-emerald-500">{{ infoEmail }}</a>
            </p>
            <p>
              <span class="text-slate-500">{{ t('contact.details.support_label') }}</span>
              <a :href="`mailto:${supportEmail}`" class="ml-2 font-semibold text-emerald-600 hover:text-emerald-500">{{ supportEmail }}</a>
            </p>
          </div>
        </div>

        <!-- Right: contextual panel -->
        <div class="lg:col-span-3">
          <p class="sr-only" role="status" aria-live="polite">{{ liveMessage }}</p>
          <Transition
            mode="out-in"
            enter-active-class="motion-safe:transition motion-safe:ease-out motion-safe:duration-150"
            enter-from-class="motion-safe:opacity-0 motion-safe:-translate-y-1"
            enter-to-class="motion-safe:opacity-100 motion-safe:translate-y-0"
            leave-active-class="motion-safe:transition motion-safe:ease-in motion-safe:duration-100"
            leave-from-class="motion-safe:opacity-100 motion-safe:translate-y-0"
            leave-to-class="motion-safe:opacity-0 motion-safe:-translate-y-1"
          >
            <div
              :key="panelKey"
              class="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8"
            >
              <!-- Logged-in organizer: hand off to support tickets -->
              <div v-if="selectedCard === 'organizer' && isOrganizer">
                <p class="text-slate-600 leading-relaxed">{{ t('contact.router.organizer.logged_in_body') }}</p>
                <div class="mt-5 flex flex-col sm:flex-row gap-3">
                  <RouterLink
                    :to="{ name: 'organizer.support.index', query: { new: '1' } }"
                    class="inline-flex items-center justify-center bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg px-6 py-3 text-sm transition-colors"
                  >
                    {{ t('contact.router.organizer.open_ticket') }}
                  </RouterLink>
                  <RouterLink
                    to="/organizer/docs"
                    class="inline-flex items-center justify-center border-2 border-slate-300 text-slate-700 hover:border-slate-400 font-semibold rounded-lg px-6 py-3 text-sm transition-colors"
                  >
                    {{ t('contact.router.organizer.browse_docs') }}
                  </RouterLink>
                </div>
              </div>

              <!-- Participant: organizer first -->
              <div v-else-if="selectedCard === 'participant' && !participantFormVisible">
                <p class="text-slate-600 leading-relaxed">{{ t('contact.router.participant.organizer_first') }}</p>
                <RouterLink :to="myRacesLink" class="mt-3 inline-block font-semibold text-sky-600 hover:text-sky-500 text-sm">
                  {{ isAuthenticated ? t('contact.router.participant.my_races') : t('contact.router.participant.my_login') }}
                </RouterLink>
                <div class="mt-6 border-t border-slate-100 pt-6">
                  <button
                    type="button"
                    class="inline-flex items-center justify-center bg-sky-600 hover:bg-sky-500 text-white font-semibold rounded-lg px-6 py-3 text-sm transition-colors"
                    @click="participantFormVisible = true"
                  >
                    {{ t('contact.router.participant.platform_issue') }}
                  </button>
                </div>
              </div>

              <!-- Contact form -->
              <div v-else-if="formTopic !== null">
                <AboutContactForm
                  :topic="formTopic"
                  :prefill-name="prefillName"
                  :prefill-email="prefillEmail"
                />
                <p v-if="selectedCard === 'organizer'" class="mt-5 text-sm text-slate-500">
                  {{ t('contact.router.organizer.login_hint_before') }}
                  <RouterLink to="/organizer/login" class="font-semibold text-emerald-600 hover:text-emerald-500">
                    {{ t('contact.router.organizer.login_hint_link') }}
                  </RouterLink>
                </p>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </section>
</template>
